import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Container, Typography, styled } from "@mui/material";
import Footer from "../componentsPeopletech/Footer/Footer";

const PrivacyContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
  backgroundColor: "#ffffff",
  maxWidth: "900px",
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "2.5rem",
  marginBottom: theme.spacing(4),
  textAlign: "center",
  color: "#000000",
  [theme.breakpoints.down("sm")]: {
    fontSize: "2rem",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "1.5rem",
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
  color: "#1c2859",
}));

const SectionText = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  lineHeight: 1.8,
  color: "#414141",
  marginBottom: theme.spacing(2),
}));

const LastUpdated = styled(Typography)(({ theme }) => ({
  fontSize: "0.9rem",
  color: "#666",
  fontStyle: "italic",
  marginBottom: theme.spacing(4),
  textAlign: "center",
}));

function dateLocaleForI18n(lang) {
  if (!lang || typeof lang !== "string") return "en-GB";
  const base = lang.split("-")[0].toLowerCase();
  if (base === "es") return "es-ES";
  if (base === "en") return lang.toLowerCase().includes("us") ? "en-US" : "en-GB";
  return "en-GB";
}

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || "en";
  const baseUrl = "https://www.peoplefortech.com";
  const dateLocale = dateLocaleForI18n(currentLang);
  const formattedDate = new Date().toLocaleDateString(dateLocale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <>
      <Helmet>
        <title>{t("privacyPolicy.pageTitle")}</title>
        <meta name="description" content={t("privacyPolicy.metaDescription")} />
        <link rel="canonical" href={`${baseUrl}/privacy-policy`} />
        <html lang={currentLang} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${baseUrl}/privacy-policy`} />
        <meta property="og:title" content={t("privacyPolicy.ogTitle")} />
        <meta property="og:description" content={t("privacyPolicy.ogDescription")} />
      </Helmet>

      <PrivacyContainer>
        <Title variant="h1">{t("privacyPolicy.heading")}</Title>
        <LastUpdated>
          {t("privacyPolicy.lastUpdatedLabel")} {formattedDate}
        </LastUpdated>

        <SectionTitle variant="h2">{t("privacyPolicy.section1Title")}</SectionTitle>
        <SectionText>{t("privacyPolicy.section1Body")}</SectionText>

        <SectionTitle variant="h2">{t("privacyPolicy.section2Title")}</SectionTitle>
        <SectionText>
          <strong>{t("privacyPolicy.section21Strong")}</strong> {t("privacyPolicy.section21Text")}
        </SectionText>
        <SectionText>
          <strong>{t("privacyPolicy.section22Strong")}</strong> {t("privacyPolicy.section22Text")}
        </SectionText>
        <SectionText>
          <strong>{t("privacyPolicy.section23Strong")}</strong> {t("privacyPolicy.section23Text")}
        </SectionText>

        <SectionTitle variant="h2">{t("privacyPolicy.section3Title")}</SectionTitle>
        <SectionText>{t("privacyPolicy.section3Intro")}</SectionText>
        <SectionText component="ul" sx={{ pl: 4 }}>
          <li>{t("privacyPolicy.section3Use1")}</li>
          <li>{t("privacyPolicy.section3Use2")}</li>
          <li>{t("privacyPolicy.section3Use3")}</li>
          <li>{t("privacyPolicy.section3Use4")}</li>
          <li>{t("privacyPolicy.section3Use5")}</li>
        </SectionText>

        <SectionTitle variant="h2">{t("privacyPolicy.section4Title")}</SectionTitle>
        <SectionText>{t("privacyPolicy.section4Intro")}</SectionText>
        <SectionText component="ul" sx={{ pl: 4 }}>
          <li>
            <strong>{t("privacyPolicy.section4ProvidersStrong")}</strong>{" "}
            {t("privacyPolicy.section4ProvidersText")}
          </li>
          <li>
            <strong>{t("privacyPolicy.section4LegalStrong")}</strong>{" "}
            {t("privacyPolicy.section4LegalText")}
          </li>
          <li>
            <strong>{t("privacyPolicy.section4ConsentStrong")}</strong>{" "}
            {t("privacyPolicy.section4ConsentText")}
          </li>
        </SectionText>

        <SectionTitle variant="h2">{t("privacyPolicy.section5Title")}</SectionTitle>
        <SectionText>{t("privacyPolicy.section5Body")}</SectionText>

        <SectionTitle variant="h2">{t("privacyPolicy.section6Title")}</SectionTitle>
        <SectionText>{t("privacyPolicy.section6Intro")}</SectionText>
        <SectionText component="ul" sx={{ pl: 4 }}>
          <li>{t("privacyPolicy.section6Right1")}</li>
          <li>{t("privacyPolicy.section6Right2")}</li>
          <li>{t("privacyPolicy.section6Right3")}</li>
          <li>{t("privacyPolicy.section6Right4")}</li>
          <li>{t("privacyPolicy.section6Right5")}</li>
          <li>{t("privacyPolicy.section6Right6")}</li>
        </SectionText>
        <SectionText>{t("privacyPolicy.section6Contact")}</SectionText>
        <SectionText>{t("privacyPolicy.section6EuComplaint")}</SectionText>

        <SectionTitle variant="h2">{t("privacyPolicy.section7Title")}</SectionTitle>
        <SectionText>{t("privacyPolicy.section7Body")}</SectionText>

        <SectionTitle variant="h2">{t("privacyPolicy.section8Title")}</SectionTitle>
        <SectionText>{t("privacyPolicy.section8Body")}</SectionText>

        <SectionTitle variant="h2">{t("privacyPolicy.section9Title")}</SectionTitle>
        <SectionText>{t("privacyPolicy.section9Body")}</SectionText>

        <SectionTitle variant="h2">{t("privacyPolicy.section10Title")}</SectionTitle>
        <SectionText>{t("privacyPolicy.section10Body")}</SectionText>

        <SectionTitle variant="h2">{t("privacyPolicy.section11Title")}</SectionTitle>
        <SectionText>{t("privacyPolicy.section11Intro")}</SectionText>
        <SectionText sx={{ fontWeight: 600, mt: 2 }}>{t("privacyPolicy.section11Email")}</SectionText>
        <SectionText>{t("privacyPolicy.section11Brand")}</SectionText>

        <SectionTitle variant="h2">{t("privacyPolicy.section12Title")}</SectionTitle>
        <SectionText>{t("privacyPolicy.section12Body")}</SectionText>
      </PrivacyContainer>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
